import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { ConfirmMessageWithNumberWrapper } from "@ryde/controls";
import {
  useHubRideActionConfirm,
  useModalConfirm,
  useModalConfirmsRide,
  useRidesBulkActions,
  useSelectedIds,
  useSelectItemsTranslate,
} from "@ryde/redux";
import {
  ConfirmResult,
  hasPastTaxiSupplierRide,
  HubRideActionKind,
  Log,
  onActivateKeyDown,
  RideDeleteActionsResultEnum,
  RideStatusEnum,
  selectedRidesSelector,
  TPermissionAction,
  useHasPermissions,
} from "@ryde/shared";

interface IEditStatusDropdownItemProps {
  status: RideStatusEnum;
  permission: TPermissionAction;
}

export const RidesEditStatusDropdownItem: FC<IEditStatusDropdownItemProps> = ({
  status,
  permission,
}) => {
  const { translateLabel } = useSelectItemsTranslate();
  const {
    bulkCancel,
    bulkReject,
    bulkStatusNew,
    bulkStatusDone,
    bulkStatusDoneApproved,
    bulkStatusReceived,
  } = useRidesBulkActions();
  const { confirmRideCancelAction } = useModalConfirmsRide();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();
  const { confirmDialogResult } = useModalConfirm();
  const { selectedRowsIds: selectedRideIds } = useSelectedIds();
  const selectedRides = useRecoilValue(selectedRidesSelector);

  const { hasPermissions: hasSpecificPermission } = useHasPermissions({
    entity: "ride",
    action: permission,
  });
  const { hasPermissions: hasUpdateStatusPermission } = useHasPermissions({
    entity: "ride",
    action: "update_status",
  });
  const isDedicatedStatusAction =
    permission === "cancel" ||
    permission === "reject" ||
    permission === "approve";
  const hasPermissions =
    hasSpecificPermission ||
    (!isDedicatedStatusAction && hasUpdateStatusPermission);

  const label = translateLabel(status);
  const { t } = useTranslation();

  const changeStatusConfirmation = async (): Promise<ConfirmResult> => {
    return confirmDialogResult({
      title: t("general.ATTENTION"),
      cancelButtonTitle: t("general.CLOSE"),
      okButtonTitle: t("general.APPROVE"),
      customContent: (
        <ConfirmMessageWithNumberWrapper>
          <span className="number">{selectedRideIds?.length}</span>
          <span className="main_message">
            {t(`ride.bulk.editStatus`, { newStatus: `"${label}"` })}
          </span>
        </ConfirmMessageWithNumberWrapper>
      ),
    });
  };

  const handleCancelClick = useEvent(async () => {
    const isHubCancelConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Cancel,
    });

    if (!isHubCancelConfirmed) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_CANCELLATION_CANCELED`),
        selectedRideIds,
      );
      return;
    }

    // RYDE-7633: a past taxi-supplier ride in the selection is canceled locally (no provider
    // notification), and the backend requires the operator's reason as its only audit trail.
    const { bookingAction: cancelRidesAction, cancelationReason } =
      await confirmRideCancelAction({
        requireCancelationReason: hasPastTaxiSupplierRide(selectedRides),
      });

    if (cancelRidesAction === RideDeleteActionsResultEnum.NoAction) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_CANCELLATION_CANCELED`),
        selectedRideIds,
      );

      return;
    }

    await bulkCancel(selectedRideIds, cancelRidesAction, cancelationReason);
  });

  const handleRejectClick = useEvent(async () => {
    const confirmationResult = await changeStatusConfirmation();

    if (confirmationResult === ConfirmResult.OK) {
      await bulkReject(selectedRideIds);
    }
  });

  const handleNewClick = useEvent(async () => {
    const confirmationResult = await changeStatusConfirmation();

    if (confirmationResult === ConfirmResult.OK) {
      await bulkStatusNew(selectedRideIds);
    }
  });

  const handleDoneClick = useEvent(async () => {
    const confirmationResult = await changeStatusConfirmation();

    if (confirmationResult === ConfirmResult.OK) {
      await bulkStatusDone(selectedRideIds);
    }
  });

  const handleDoneApprovedClick = useEvent(async () => {
    const confirmationResult = await changeStatusConfirmation();

    if (confirmationResult === ConfirmResult.OK) {
      await bulkStatusDoneApproved(selectedRideIds);
    }
  });

  const handleReceivedClick = useEvent(async () => {
    const confirmationResult = await changeStatusConfirmation();

    if (confirmationResult === ConfirmResult.OK) {
      await bulkStatusReceived(selectedRideIds);
    }
  });

  const onClickOption = useEvent(() => {
    if (!hasPermissions) {
      return;
    }

    switch (status) {
      case RideStatusEnum.Canceled:
        handleCancelClick();
        break;

      case RideStatusEnum.Rejected:
        handleRejectClick();
        break;

      case RideStatusEnum.New:
        handleNewClick();
        break;

      case RideStatusEnum.Done:
        handleDoneClick();
        break;

      case RideStatusEnum.DoneApproved:
        handleDoneApprovedClick();
        break;

      case RideStatusEnum.Received:
        handleReceivedClick();
        break;

      default:
        alert("This status is not implemented yet");
    }
  });

  const disabledStatusesForNow = useMemo(() => {
    return (
      /*status === RideStatusEnum.New ||*/ status === RideStatusEnum.Approved
    );
  }, [status]);

  return (
    <div
      className={clsx("option_item", {
        disabled: !hasPermissions || disabledStatusesForNow,
      })}
      onClick={onClickOption}
      onKeyDown={onActivateKeyDown(onClickOption)}
      role="button"
      tabIndex={0}
    >
      {label}
    </div>
  );
};
